/**
 * 功能权限控制
 */
import { autorun, makeAutoObservable, runInAction } from 'mobx'
import { PayApi, ThirdPartyApi } from 'api'
import { cloneDeep, filter, find, findIndex, forEach, groupBy, keyBy, map, sortBy, toArray } from '@dt/fe-utils/dist/lodash'
import { RESTAURANT_EVENT, restaurantCenter, cartMobx, productMobx, orderMobx, discountMobx } from 'restaurant'
import appMobx from '../app/app.mobx'
import { store, toDecimal, uuid } from '@dt/fe-utils'
import CashierController from '@ctrls/cashierController'
import { getHistoryNew, localStore, showMsg, toast } from '@utils'
import RestoreOrderController from '@ctrls/restoreOrderController'
import { TChannelEatType } from 'restaurant/mobx/store.mobx'

export enum TThirdType {
  douyin = 'douyin',
  // meituan = 'meituan',
  // koubei = 'Koubei'
}

export enum TThirdChannelsType {
  douyin = 'DouyinCoupon',
  meituan = 'MeituanCoupon',
  koubei = 'KoubeiCoupon'
}

/** 查找当前购物车规格餐品,已关联券的数量  */
function getRelationCartItemCnt (cartItem: IViewCartItem | IViewOrderDetail, list: IThirdCoupon[]): number {
  let cnt = 0
  forEach(list, (item) => {
    if (item.sku.productNo === cartItem.productNo && item.sku.productScaleNo === cartItem.scaleNo) {
      cnt += 1
    }
  })
  return cnt
}

/** 查找当前购物车规格餐品,未关联券的数量  */
function getUnRelationCartItemCnt (cartItem: IViewCartItem | IViewOrderDetail, list: IThirdCoupon[]): number {
  const useCnt = getRelationCartItemCnt(cartItem, list)
  return (cartItem.cnt - useCnt) || 0
}

class ThirdPartyMobx {
  constructor () {
    makeAutoObservable(this)
    restaurantCenter.on(RESTAURANT_EVENT.MENU_START, () => {
      this.getStorage()
    })
    restaurantCenter.on(RESTAURANT_EVENT.CART_ITEMS_CHANGED, () => {
      this.calcAutoChangeCouponToCart()
    })
  }
  list: IThirdSetting[] = []
  listByKey: { [key: string]: IThirdSetting } = {}
  /** 当前录入的券 */
  couponList: IThirdCoupon[] = []

  doCoupon: IThirdCoupon

  /** 当前选中的券 */
  checkCoupons: IThirdCoupon[] = []

  /** 已经从缓存拿过数据，就不拿了 */
  isInitStorage: boolean = false

  /** 从订单内计算当前使用餐券的金额 */
  orderCouponMealUseMap: any = {}

  /** 本地缓存couponCode的map */
  get checkCouponsMap () {
    return keyBy(this.checkCoupons, 'couponCode') as {
      [couponCode: string]: IThirdCoupon;
    }
  }

  /** 本地缓存couponCode的map */
  get couponCodeMap () {
    return keyBy(this.couponList, 'couponCode') as {
      [pk: string]: IThirdCoupon;
    }
  }

  /** 根据productNo和scaleNo 把券打包 */
  get noToMealCouponsMap () {
    return groupBy(filter(this.couponList, item => item.sku.grouponType !== 2 && !!item.sku.productNo && !!item.sku.productScaleNo), (item) => {
      return item.sku.productNo + item.sku.productScaleNo
    }) as {
      [pk: string]: IThirdCoupon[];
    }
  }

  /** 当前已经选中的餐品券 根据productNo和scaleNo 把券打包 */
  get noToCheckMealCouponsMap () {
    return groupBy(filter(this.checkCoupons, item => item.sku.grouponType !== 2 && !!item.sku.productNo && !!item.sku.productScaleNo), (item) => {
      return item.sku.productNo + item.sku.productScaleNo
    }) as {
      [pk: string]: IThirdCoupon[];
    }
  }

  /** 选中的现金券 code map */
  get codeToMoneyCouponsMap () {
    return keyBy(filter(this.checkCoupons, item => item.sku.grouponType === 2), 'couponCode') as {
      [pk: string]: IThirdCoupon;
    }
  }

  /** 查看当前渠道就餐方式是否配置 */
  get checkCanUseToChannelEatType () {
    const channel = cartMobx.channel
    const eatType = cartMobx.info.eatType
    const CEKey: string = `${channel}${eatType}`
    let canConf: any = null
    for (let key in this.listByKey) {
      if (this.listByKey[key]?.[CEKey]) {
        if (!canConf) canConf = {}
        canConf[key] = 1
      }
    }
    return canConf
  }

  // 初始化餐品券
  get couponMealUseMap () {
    const proList = [...cartMobx.viewCartItems, ...cartMobx.viewOrderItems]
    const discountItemMap: any = cloneDeep(discountMobx.discountResult?.itemMap || {})
    const resList: any = {}
    const usedCouponCode = []
    proList.forEach((item: any) => {
      const noKey = item.productNo + item.scaleNo
      // 餐品与券匹配
      const checkCoupons = this.noToCheckMealCouponsMap[noKey]
      const discountItem = discountItemMap?.[item.pk]
      if (checkCoupons) {
        // 总优惠金额
        let offPrice: number = item.offPrice
        const allCouponCnt = checkCoupons.length
        const appOff = discountItem?.apportion?.off || 0
        const restItemPrice = toDecimal((item.totalProductPrice - offPrice - appOff) / (item.cnt))
        for (let i = 0; i < allCouponCnt; i++) {
          const couponCode = checkCoupons[i].couponCode
          const paidFee: number = toDecimal(restItemPrice)
          if (restItemPrice > 0 && !usedCouponCode.includes(couponCode)) {
            if (resList[noKey]) {
              resList[noKey].push({
                paidFee: paidFee,
                productScaleShortNo: noKey,
                productPk: item.pk,
                couponCode
              })
            } else {
              resList[noKey] = [
                {
                  paidFee: paidFee,
                  productScaleShortNo: noKey,
                  productPk: item.pk,
                  couponCode
                }
              ]
            }
            usedCouponCode.push(couponCode)
          }
        }
      }
    })
    for (let key in resList) {
      resList[key] = resList[key].sort((a: any, b: any) => b.paidFee - a.paidFee)
    }
    return resList
  }

  get couponResult (): { offPrice: number; couponArr: IThirdCouponPrePay[] } {
    // 实付总价
    const paidFee = cartMobx.cartResult.itemTotalPrice
    // 0元支付不需要核券
    if (paidFee <= 0 || !this.checkCanUseToChannelEatType || !this.checkCoupons.length) {
      return {
        offPrice: 0,
        couponArr: []
      }
    }

    /** 餐品券 */
    const couponMealMap = this.couponMealUseMap
    /** 现金券 */
    const couponMoneyMap = this.codeToMoneyCouponsMap
    /** 已用优惠券整合 */
    const couponArr = []
    /** 当前已经选择的券 */
    const checkCouponsMap = this.checkCouponsMap
    /** 总的抵扣金额 */
    let offPrice = 0

    /** 计算每个券抵扣金额后剩余实付金额 */
    let paidFeeOff = paidFee

    // 餐品券
    for (let noKey in couponMealMap) {
      const coupons = couponMealMap[noKey]
      for (let i = 0; i < coupons.length; i++) {
        const coupon = coupons[i]
        const couponDetail = checkCouponsMap[coupon.couponCode]
        if (couponDetail) {
          offPrice += coupon.paidFee
          couponArr.push({
            channel: TThirdChannelsType[couponDetail.type],
            price: coupon.paidFee,
            encryptedCode: couponDetail.encryptedCode,
            couponCode: couponDetail.couponCode,
            type: couponDetail.sku.grouponType,
            thirdId: couponDetail.sku.thirdSkuId,
            couponPayAmount: couponDetail.amount.couponPayAmount / 100
          })
        }
      }
    }
    paidFeeOff = toDecimal(paidFeeOff - offPrice)
    // 餐品券抵扣完后还有剩余金额，需要继续走现金券
    if (paidFeeOff > 0) {
      for (let couponCode in couponMoneyMap) {
        const couponDetail = checkCouponsMap[couponCode]
        if (paidFeeOff > 0 && couponDetail) {
          const marketPrice = toDecimal(couponDetail.sku.marketPrice / 100)
          const _padFee = marketPrice > paidFeeOff ? paidFeeOff : marketPrice
          offPrice += _padFee
          paidFeeOff = toDecimal(paidFeeOff - _padFee)
          couponArr.push({
            couponType: couponDetail.type,
            channel: TThirdChannelsType[couponDetail.type],
            price: _padFee,
            encryptedCode: couponDetail.encryptedCode,
            couponCode,
            type: couponDetail.sku.grouponType,
            thirdId: couponDetail.sku.thirdSkuId,
            couponPayAmount: couponDetail.amount.couponPayAmount / 100
          })
          break
        }
      }
    }
    return {
      offPrice,
      couponArr
    }
  }

  async init () {
    try {
      const { result } = await ThirdPartyApi.getThirdCouponConfig()
      if (result?.content?.length) {
        let _list = [...result?.content].map((item) => {
          const { withOtherDiscounts = 0, checkByMobile = 0, channelEatType = [], channel } = item
          const channelEatTypes: { [key: string]: 0 | 1 } = {}
          if (checkByMobile) {
            forEach(channelEatType, item => {
              channelEatTypes[`${item.channel}${item.eatType}`] = 1
            })
          }
          return {
            channel,
            withOtherDiscounts,
            checkByMobile,
            [TChannelEatType.selfDeliverypickup]: 0,
            [TChannelEatType.selfDeliverydelivery]: 0,
            [TChannelEatType.mobiledinein]: 0,
            [TChannelEatType.mobiletogo]: 0,
            ...channelEatTypes
          } as IThirdSetting
        })
        runInAction(() => {
          this.list = [..._list]
          this.listByKey = keyBy(_list, 'channel')
        })
        return true
      }
    } catch (error) {
      console.error(error)
    }
    return false
  }

  /** 预验券 */
  async getCouponDetail (params: {
    couponCode: string;
    type: 'meituan' | 'douyin' | 'Koubei';
  }): Promise<IThirdCoupon | undefined> {
    try {
      const para: any = {
        clientNumber: appMobx.deviceId,
        useType: 'dc',
        bo,
        requestId: uuid(),
        brandId: Config.BRANDID,
        storeId: Config.STOREID,
        channelType: {
          [TThirdChannelsType[params.type]]: {
            ext: {
              couponCode: params.couponCode
            },
          }
        }
      }
      const res: any = await PayApi.getCouponDetail(para)
      if (res.error) {
        toast({
          message: `${i18n.t('pay.errorCouponTip1')}<br/>${i18n.t('pay.errorCouponTip1_1')}`,
          time: 2000,
          position: 'middle',
          cssClass: 'toastW50'
        })
      } else if (res?.result?.list?.length) {
        const couponList = [...this.couponList]
        const coupon = {
          ...res?.result?.list[0],
          arr: res?.result?.list,
          type: params.type,
          couponCode: params.couponCode,
          couponCnt: res?.result?.list.length
        }
        const cIndex = findIndex(couponList, (item) => coupon.couponCode === item.couponCode)
        if (cIndex >= 0) {
          couponList[cIndex] = coupon
        } else {
          couponList.push(coupon)
        }
        runInAction(() => {
          this.couponList = [...couponList]
          this.saveStorage(couponList)
        })
        return coupon
      }
    } catch (e) {
    }
    return
  }

  /** 批量预验券 */
  getCouponDetailList (type: TThirdType, couponList: IThirdCoupon[]) {
    const para: any = {
      clientNumber: appMobx.deviceId,
      useType: 'dc',
      bo,
      requestId: uuid(),
      brandId: Config.BRANDID,
      storeId: Config.STOREID,
      channelType: {
        [TThirdChannelsType[type]]: {
          ext: {
            // 批量验券取前20条
            couponCode: map(couponList.slice(0, 20), (item) => item.couponCode).join(',')
          },
        }
      }
    }
    return PayApi.getCouponDetailList(para)
  }

  /** 自动选中在购物车内的餐品关联的三方券 */
  calcAutoChangeCouponToCart () {
    const pendingOrdersId = store.get(RestoreOrderController.localKey())
    if (pendingOrdersId) return
    // 如果当前已经有选中的券，就不要自动选择了
    if (cartMobx.cartResult.paidPrice <= 0 && !this.checkCanUseToChannelEatType) {
      this.setCheck([])
      return
    }
    const discountItemMap: any = cloneDeep(discountMobx.discountResult?.itemMap || {})

    const checkCouponsMap: { [couponCode: string]: IThirdCoupon } = {}
    // 实付总价
    let totalPaidFee = cartMobx.cartResult.itemTotalPrice
    forEach([...cartMobx.viewCartItems, ...cartMobx.viewOrderItems], (cartItem) => {
      const noKey = cartItem.productNo + cartItem.scaleNo
      const couponArr = this.noToMealCouponsMap[noKey]
      const discountItem = discountItemMap?.[cartItem.pk]
      // 总优惠金额
      let offPrice: number = cartItem.offPrice
      const appOff = discountItem?.apportion?.off || 0
      let unUseCnt = cartItem.cnt
      // 单个餐品实付 （餐品原价 - 单品折扣 - 整单分摊折扣）
      const restItemPrice = toDecimal((cartItem.totalProductPrice - offPrice - appOff) / (cartItem.cnt))
      forEach(couponArr, (coupon) => {
        if (coupon.sku.grouponType !== 2 && unUseCnt > 0 && !checkCouponsMap[coupon.couponCode]) {
          totalPaidFee = toDecimal(totalPaidFee - restItemPrice)
          checkCouponsMap[coupon.couponCode] = coupon
          --unUseCnt
        }
      })
    })
    if (totalPaidFee > 0) {
      const moneyCoupons = sortBy(filter(this.couponList, coupon => coupon.sku.grouponType === 2), (coupon) => coupon.sku.marketPrice)
      // 找到高于实付金额的券index
      const maxCouponIndex = findIndex(moneyCoupons, (coupon) => coupon.sku.marketPrice >= totalPaidFee * 100)
      if (maxCouponIndex >= 0) {
        const maxCoupon = moneyCoupons?.[maxCouponIndex]
        checkCouponsMap[maxCoupon.couponCode] = maxCoupon
      } else {
        for (let i = moneyCoupons.length - 1; i >= 0; i--) {
          if (totalPaidFee > 0) {
            const coupon = moneyCoupons[i]
            const restItemPrice = toDecimal(coupon.sku.marketPrice / 100)
            checkCouponsMap[coupon.couponCode] = coupon
            totalPaidFee -= restItemPrice
            break
          } else {
            break
          }
        }
      }
    }
    const checkCoupons = toArray(checkCouponsMap)
    this.setCheck(checkCoupons)
  }

  /** 保存选中 */
  setCheck (coupons: IThirdCoupon[]) {
    this.checkCoupons = [...coupons]
  }

  /** 从购物车获取指定餐品 */
  getCartItem (productNo: string, scaleNo: string) {
    return [...cartMobx.viewCartItems, ...cartMobx.viewOrderItems].filter(item => item.productNo === productNo && item.scaleNo === scaleNo)
  }

  /** 选中单张券 */
  checkCoupon (info: IThirdCoupon) {
    const history = getHistoryNew()
    // 餐品券当前菜单内是否有该餐品
    const grouponType = info.sku.grouponType
    if (grouponType !== 2) {
      const hasProduct = find(productMobx.viewProducts, (item) => item.no === info.sku.productNo)
      if (!hasProduct) {
        showMsg(i18n.t('pay.errorCouponTip2'), 2000)
        return
      }
    }
    let couponList = [...this.checkCoupons]
    const cIndex = findIndex(couponList, (item) => info.couponCode === item.couponCode)
    const isAdd = cIndex < 0
    if (isAdd) {
      const sku = info.sku
      const isProductCoupon = sku.grouponType !== 2
      if (isProductCoupon) {
        const viewCartItems = this.getCartItem(info.sku.productNo, info.sku.productScaleNo)
        // 关联餐品在购物车内有未关联的数量
        let unUseCnt = 0
        forEach(viewCartItems, (viewCart) => {
          unUseCnt = getUnRelationCartItemCnt(viewCart, this.checkCoupons) + unUseCnt
        })
        if (!unUseCnt) {
          const product = productMobx.noToViewProductMap[info.sku.productNo] as IViewProduct
          CashierController.handleProductToCart({
            type: 'add',
            product,
            thirdCouponCheckCB: (data) => {
              if (data.from === 'auto') {
                toast({
                  message: `${product.name}<br/>${i18n.t('menu.thirdCouponChangeOneScaleProductTips1')}`,
                  time: 2000,
                  position: 'middle',
                  cssClass: 'toastW50'
                })
              }
              if (data.type === 'addCart') {
                history.goBack()
              }
            }
          })
        } else {
          couponList.push(info)
        }
      } else {
        couponList = filter(couponList, (item) => item.sku.grouponType !== 2)
        couponList.push(info)
      }
    } else {
      couponList.splice(cIndex, 1)
    }
    this.setCheck(couponList)
  }

  /** 选中券 info 券信息， isSave 是否直接保存到mobx内 */
  setSelectCoupon (product: IDoProduct | IDoCombo, couponCode: string) {
    const couponInfo = find(this.couponList, (item) => couponCode === item.couponCode)
    const isRightProduct = couponInfo.sku.productNo === product.no && couponInfo.sku.productScaleNo === product.scaleNo
    if (isRightProduct) {
      const couponList = [...this.checkCoupons]
      couponList.push(couponInfo)
      this.setCheck(couponList)
    }
  }

  /** 清除已选中券 */
  clear (type: 'check' | 'all' = 'check') {
    let _coupons = []
    if (type === 'check') {
      let couponCodes = this.couponResult.couponArr.map((item) => item.couponCode)
      _coupons = this.couponList.filter((item) => !couponCodes.includes(item.couponCode))
    }
    this.couponList = [..._coupons]
    this.saveStorage(_coupons)
  }

  /** 获取本地 */
  async getStorage () {
    if (this.isInitStorage) return
    const list = localStore.get(`ls.thirdCoupons.${Config.BRANDID}`) || []
    if (list.length > 0) {
      const listGroup = groupBy(list, 'type')
      const _coupons = []
      if (listGroup[TThirdType.douyin]) {
        const res = await this.getCouponDetailList(TThirdType.douyin, listGroup[TThirdType.douyin])
        if (res.result) {
          /**
           * 接口结构
           * result: [
           *  {
           *    list: [],
           *    couponCode: ''
           *  }
           * ]
           */
          forEach(res.result || [], (item) => {
            if (item.list?.length) {
              _coupons.push({
                ...item.list[0],
                couponCode: item.couponCode,
                arr: item.list,
                type: TThirdType.douyin,
                couponCnt: item.list.length
              })
            }
          })
        }
      }
      this.couponList = cloneDeep(_coupons)
      this.isInitStorage = true
      this.saveStorage(_coupons)
    }
  }

  /** 保存券数据到本地，有效期到当天24点 */
  saveStorage (list: IThirdCoupon[]) {
    localStore.set(`ls.thirdCoupons.${Config.BRANDID}`, list)
  }
}

// 单品券 101058642307842,次卡 101096424742594,套餐 120108862621762,现金 101109297828226,单品有特殊预约 酱鸭 101094443875618
// 101411399966082, 101118339290914
const thirdPartyMobx = new ThirdPartyMobx()

let discountSk = ''
let cartPaidFee = 0

autorun(() => {
  if (discountMobx.discountResult.sk !== discountSk && cartPaidFee !== cartMobx.cartResult.paidPrice && orderMobx.order?.status !== 'Paying') {
    thirdPartyMobx.calcAutoChangeCouponToCart()
    discountSk = discountMobx.discountResult.sk
    cartPaidFee = cartMobx.cartResult.paidPrice
  }
})

export default thirdPartyMobx
;(window as any).thirdPartyMobx = thirdPartyMobx
